import type { ComponentType } from 'react'
import { lazy } from 'react'

const RELOAD_KEY = 'checkout:chunk-reload-at'
const RETRY_AFTER_MS = 60_000

export const isChunkLoadError = (error: unknown): boolean => {
    if (!(error instanceof Error)) return false
    const message = error.message || ''
    return (
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('error loading dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('is not a valid JavaScript MIME type') ||
        message.includes('Unable to preload CSS')
    )
}

// Trigger at most one full page reload per RETRY_AFTER_MS window when a stale
// chunk (typical after a redeploy) fails to import, fetching a fresh index.html
// with current chunk hashes. The timestamped guard prevents reload loops while
// still allowing recovery if another deploy happens later in the same session.
// Returns true when a reload was started.
export const reloadOnceOnStaleChunk = (error: unknown): boolean => {
    if (!isChunkLoadError(error)) return false

    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
    if (Date.now() - lastReloadAt < RETRY_AFTER_MS) return false

    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
    window.location.reload()
    return true
}

// Wrap React.lazy so that a stale-chunk import recovers via the one-reload
// guard above instead of surfacing a chunk-load error.
export const lazyWithRetry = <T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>,
) => {
    return lazy(async () => {
        try {
            return await factory()
        } catch (error) {
            if (!reloadOnceOnStaleChunk(error)) throw error

            return new Promise<{ default: T }>(() => {
                // Stall the Suspense boundary while the reload happens.
            })
        }
    })
}
